import { useMemo } from 'react'
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell
} from 'recharts'
import { ManualTestTrade } from '../types'
import { useI18n } from '../i18n'

interface ManualTestChartsProps {
  trades: ManualTestTrade[]
  onRValueRangeClick?: (range: string) => void
}

export default function ManualTestCharts({ trades, onRValueRangeClick }: ManualTestChartsProps) {
  const t = useI18n('zh').manualTest

  // R值分布数据
  const rValueDistribution = useMemo(() => {
    if (trades.length === 0) return []

    const ranges = [
      { range: '-∞ ~ -2', min: -Infinity, max: -2, count: 0 },
      { range: '-2 ~ -1', min: -2, max: -1, count: 0 },
      { range: '-1 ~ 0', min: -1, max: 0, count: 0 },
      { range: '0 ~ 1', min: 0, max: 1, count: 0 },
      { range: '1 ~ 2', min: 1, max: 2, count: 0 },
      { range: '2 ~ ∞', min: 2, max: Infinity, count: 0 }
    ]

    trades.forEach(trade => {
      const rValue = trade.pnl_r
      for (const range of ranges) {
        if (rValue >= range.min && rValue < range.max) {
          range.count++
          break
        }
      }
    })

    return ranges
  }, [trades])

  // 盈亏分布数据
  const pnlDistribution = useMemo(() => {
    return trades.map((trade, index) => ({
      tradeNumber: index + 1,
      pnl: trade.pnl_amount,
      date: new Date(trade.entry_time).toLocaleDateString(),
      direction: trade.direction,
      manualOverride: trade.manual_override
    }))
  }, [trades])

  // 资金曲线数据
  const equityCurve = useMemo(() => {
    if (trades.length === 0) return []

    let cumulativePnL = 0
    return trades.map((trade, index) => {
      cumulativePnL += trade.pnl_amount
      return {
        tradeNumber: index + 1,
        cumulativePnL,
        date: new Date(trade.entry_time).toLocaleDateString(),
        rValue: trade.pnl_r
      }
    })
  }, [trades])

  // 自定义工具提示
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white dark:bg-gray-800 p-3 border border-gray-200 dark:border-gray-600 rounded shadow-lg">
          <p className="font-medium text-gray-900 dark:text-white">{`${label}`}</p>
          {payload.map((entry: any, index: number) => (
            <p key={index} style={{ color: entry.color }} className="text-gray-700 dark:text-gray-300">
              {`${entry.name}: ${entry.value}`}
            </p>
          ))}
        </div>
      )
    }
    return null
  }

  // R值分布图点击处理
  const handleBarClick = (data: any) => {
    if (onRValueRangeClick) {
      onRValueRangeClick(data.range)
    }
  }

  return (
    <div className="space-y-8">
      {/* R值分布直方图 */}
      <div className="card p-6">
        <h3 className="text-lg font-semibold mb-4">{t.charts.rValueDistribution}</h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={rValueDistribution}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="range" />
            <YAxis />
            <Tooltip content={<CustomTooltip />} />
            <Bar 
              dataKey="count" 
              fill="#3B82F6"
              onClick={handleBarClick}
              style={{ cursor: 'pointer' }}
            >
              {rValueDistribution.map((entry, index) => (
                <Cell 
                  key={`cell-${index}`} 
                  fill={entry.count > 0 ? '#3B82F6' : '#E5E7EB'} 
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* 盈亏金额分布图 */}
      <div className="card p-6">
        <h3 className="text-lg font-semibold mb-4">{t.charts.pnlDistribution}</h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={pnlDistribution}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="tradeNumber" />
            <YAxis />
            <Tooltip content={<CustomTooltip />} />
            <Bar dataKey="pnl">
              {pnlDistribution.map((entry, index) => (
                <Cell 
                  key={`cell-${index}`} 
                  fill={entry.pnl >= 0 ? '#10B981' : '#EF4444'} 
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* 资金曲线图 */}
      <div className="card p-6">
        <h3 className="text-lg font-semibold mb-4">{t.charts.equityCurve}</h3>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={equityCurve}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="tradeNumber" />
            <YAxis />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Line 
              type="monotone" 
              dataKey="cumulativePnL" 
              stroke="#3B82F6" 
              strokeWidth={2}
              dot={{ fill: '#3B82F6', strokeWidth: 2, r: 4 }}
              activeDot={{ r: 6 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* 图表说明 */}
      <div className="bg-blue-50 p-4 rounded-lg">
        <h4 className="font-medium text-blue-900 mb-2">图表说明</h4>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• <strong>R值分布图</strong>：显示不同R值区间的交易数量分布，点击柱状图可筛选对应区间的交易记录</li>
          <li>• <strong>盈亏分布图</strong>：按交易顺序显示每笔交易的盈亏金额，绿色表示盈利，红色表示亏损</li>
          <li>• <strong>资金曲线图</strong>：显示累计盈亏变化趋势，反映策略的整体表现</li>
        </ul>
      </div>
    </div>
  )
} 